import { Component, computed, input, model, output, signal } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { IconPriceDiscountComponent } from '@pedix-workspace/angular-ui-icons';
import { CurrencyFormatPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import {
  ShippingEstimate,
  DimensionsExceededInfo,
  getStateIdFromPostalCode,
} from '@pedix-workspace/utils';
import {
  ShippingCostResponseParcel,
  hasMinimumPurchaseForFreeShipping,
  hasDeliveryCostToBeDefined,
  hasFreeShippingGrantedAmount,
  hasMinimumPurchaseErrorAmount,
  hasShippingEstimateError,
  hasPostalCodeRequired,
  hasAvailableParcelOptions,
} from '@pedix-workspace/shared-delivery';
import { ShippingNotAllowedComponent } from '../shipping-not-allowed/shipping-not-allowed.component';
import { ShippingContinueWithConfirmationComponent } from '../shipping-continue-with-confirmation/shipping-continue-with-confirmation.component';
import { ShippingOptionsSelectorComponent } from '../../shipping-options-selector/shipping-options-selector.component';
import {
  BranchSelectorComponent,
  CorreoArgentinoAgency,
} from '../../branch-selector/branch-selector.component';

@Component({
  selector: 'pxw-shipping-cost-response-parcel',
  standalone: true,
  templateUrl: './shipping-cost-response-parcel.component.html',
  styleUrls: ['./shipping-cost-response-parcel.component.scss'],
  imports: [
    TranslocoDirective,
    AlertComponent,
    FormsModule,
    CurrencyFormatPipe,
    IconPriceDiscountComponent,
    ShippingNotAllowedComponent,
    ShippingContinueWithConfirmationComponent,
    ShippingOptionsSelectorComponent,
    BranchSelectorComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class ShippingCostResponseParcelComponent {
  readonly shippingCostResponse = input.required<ShippingCostResponseParcel>();
  readonly isFormSubmitted = input.required<boolean>();
  readonly selectedParcelOption = input<ShippingEstimate | null>(null);
  readonly apiUrl = input<string>('');
  readonly establishmentId = input<string>('');
  readonly postalCode = input<string>('');
  readonly userCoordinates = input<{ lat: number; lng: number } | null>(null);

  readonly outOfDeliveryZoneConfirmed = model.required<boolean>();

  readonly selectedParcelOptionChange = output<ShippingEstimate | null>();
  readonly selectedBranchChange = output<CorreoArgentinoAgency | null>();

  readonly selectedBranch = signal<CorreoArgentinoAgency | null>(null);

  readonly requiresBranchSelection = computed(() => {
    const selected = this.selectedParcelOption();
    return selected?.deliveryType === 'S';
  });

  readonly branchSelectionValid = computed(() => {
    if (!this.requiresBranchSelection()) {
      return true;
    }
    return this.selectedBranch() !== null;
  });

  readonly stateIdFromPostalCode = computed(() => {
    const postalCode = this.postalCode();
    if (!postalCode) return undefined;
    return getStateIdFromPostalCode(postalCode);
  });

  protected readonly hasMinimumPurchaseForFreeShipping = hasMinimumPurchaseForFreeShipping;
  protected readonly hasDeliveryCostToBeDefined = hasDeliveryCostToBeDefined;
  protected readonly hasFreeShippingGrantedAmount = hasFreeShippingGrantedAmount;
  protected readonly hasMinimumPurchaseErrorAmount = hasMinimumPurchaseErrorAmount;
  protected readonly hasShippingEstimateError = hasShippingEstimateError;
  protected readonly hasPostalCodeRequired = hasPostalCodeRequired;
  protected readonly hasAvailableParcelOptions = hasAvailableParcelOptions;

  readonly dimensionsExceededInfo = computed<DimensionsExceededInfo | null>(() => {
    const response = this.shippingCostResponse();
    if (!hasShippingEstimateError(response)) return null;

    const estimate = response.shippingEstimate;
    if (!estimate?.additionalInfo) return null;

    try {
      const info = JSON.parse(estimate.additionalInfo);
      if (info.dimensionsExceeded) {
        return {
          dimensionsExceeded: true,
          calculatedDimensions: info.calculatedDimensions,
          exceededLimits: info.exceededLimits,
        };
      }
    } catch {
      return null;
    }

    return null;
  });

  onParcelOptionSelected(option: ShippingEstimate | null) {
    this.selectedParcelOptionChange.emit(option);
    if (option?.deliveryType !== 'S') {
      this.selectedBranch.set(null);
      this.selectedBranchChange.emit(null);
    }
  }

  onBranchSelected(agency: CorreoArgentinoAgency | null) {
    this.selectedBranch.set(agency);
    this.selectedBranchChange.emit(agency);
  }
}
